import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Mic, Send, Copy, Check, X, Sparkles, Home, Users, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import LiteShell, { StepIndicator } from '../LiteShell';
import DeIdPanel from '@/components/lite/DeIdPanel';
import RateLimitGate from '@/components/lite/RateLimitGate';
import DictationError, { IframeDictationNotice } from '@/components/lite/DictationError';
import LiteFeedback from '@/components/lite/LiteFeedback';
import useDictation from '@/hooks/useDictation';
import { liteApi } from '@/utils/lite/liteApi';

const COMMUNICATION_PROFILES = [
  'GLP / gestalt language processor',
  'AAC user',
  'Expressive language delay',
  'Receptive language difficulties',
  'Speech sound difficulties',
  'Fluency',
  'Social communication / pragmatics',
  'Mixed profile',
  'Other / not specified',
];

const REGULATION_OPTIONS = [
  'Highly regulated and engaged',
  'Intermittently engaged',
  'Sensory-seeking',
  'Dysregulated at transitions',
  'Avoidant / low engagement',
  'Fluctuating regulation',
];

const DURATION_OPTIONS = [
  { value: 'brief', label: 'Brief (<20 min)' },
  { value: '30min', label: '30 minutes' },
  { value: '45min', label: '45 minutes' },
  { value: '60min', label: '60 minutes' },
];

const INTEREST_SUGGESTIONS = [
  'bubbles', 'trains', 'spinning', 'music', 'books', 'water play',
  'cars', 'animals', 'dinosaurs', 'letters', 'numbers', 'sensory bins',
];

// Parse the Markdown output into its 4 sections
function parseSections(text) {
  const result = {
    clinical_reflection: '',
    next_session_focus: '',
    parent_handoff: '',
    home_carryover: '',
  };
  if (!text) return result;
  const patterns = [
    { key: 'clinical_reflection', re: /##\s*Clinical reflection\s*\n([\s\S]*?)(?=\n##\s|$)/i },
    { key: 'next_session_focus', re: /##\s*Suggested next session focus\s*\n([\s\S]*?)(?=\n##\s|$)/i },
    { key: 'parent_handoff', re: /##\s*Parent handoff\s*\n([\s\S]*?)(?=\n##\s|$)/i },
    { key: 'home_carryover', re: /##\s*Home carry-?over idea\s*\n([\s\S]*?)(?=\n##\s|$)/i },
  ];
  for (const { key, re } of patterns) {
    const m = text.match(re);
    if (m) result[key] = m[1].trim();
  }
  return result;
}

export default function SessionToHomeBridge() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    session_notes: '',
    interests: [],
    interest_input: '',
    communication_profile: '',
    regulation: '',
    parent_input: '',
    session_duration: '30min',
  });
  const [cleanedNotes, setCleanedNotes] = useState('');
  const [output, setOutput] = useState('');
  const [sections, setSections] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState({ used: 0, limit: 0 });

  // Email-to-parent modal state
  const [showParentEmail, setShowParentEmail] = useState(false);
  const [parentEmail, setParentEmail] = useState('');
  const [clinicianName, setClinicianName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [sendingParent, setSendingParent] = useState(false);

  const [copiedKey, setCopiedKey] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addInterest = (txt) => {
    const trimmed = (txt || '').trim();
    if (!trimmed) return;
    if (form.interests.includes(trimmed)) return;
    update('interests', [...form.interests, trimmed]);
    update('interest_input', '');
  };
  const removeInterest = (i) => update('interests', form.interests.filter((_, idx) => idx !== i));

  const {
    recordingField,
    error: dictationError,
    supported: voiceSupported,
    inIframe: dictationInIframe,
    start: startDictation,
    dismissError: dismissDictationError,
  } = useDictation({
    onTranscript: (fieldKey, transcript) => {
      if (fieldKey === 'session_notes') {
        setForm((f) => ({ ...f, session_notes: (f.session_notes ? f.session_notes + ' ' : '') + transcript }));
      } else if (fieldKey === 'parent_input') {
        setForm((f) => ({ ...f, parent_input: (f.parent_input ? f.parent_input + ' ' : '') + transcript }));
      }
    },
    maxSeconds: 30,
  });

  const buildNotesText = () => {
    const lines = [];
    if (form.session_notes.trim()) lines.push(`Session observations: ${form.session_notes.trim()}`);
    if (form.interests.length) lines.push(`Child interests today: ${form.interests.join(', ')}`);
    if (form.communication_profile) lines.push(`Communication profile: ${form.communication_profile}`);
    if (form.regulation) lines.push(`Regulation / engagement: ${form.regulation}`);
    if (form.parent_input.trim()) lines.push(`Parent concerns: ${form.parent_input.trim()}`);
    return lines.join('\n\n');
  };

  const goToDeId = () => {
    setError('');
    if (!form.session_notes.trim() || form.session_notes.trim().length < 20) {
      setError('Please add at least a sentence or two about what happened in the session.');
      return;
    }
    setStep(2);
  };

  const proceedAfterDeId = async (cleaned) => {
    setCleanedNotes(cleaned);
    setStep(3);
    await runGenerate();
  };

  const runGenerate = async () => {
    setOutput('');
    setSections(null);
    setError('');
    try {
      let email = null;
      try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
      const payload = {
        session_notes: form.session_notes,
        interests: form.interests,
        communication_profile: form.communication_profile,
        regulation: form.regulation,
        parent_input: form.parent_input,
        session_duration: form.session_duration,
        confirm_deidentified: true,
        email,
      };
      const res = await liteApi.generateSessionBridge(payload);
      setOutput(res.output);
      setSections(parseSections(res.output));
      setUsage({ used_today: res.used_today, daily_limit: res.daily_limit });
      setStep(4);
    } catch (e) {
      if (e.status === 429) {
        const d = e.data?.detail || {};
        setGateInfo({ used: d.used || 0, limit: d.limit || 5 });
        setGateOpen(true);
        setStep(1);
      } else {
        setError(e.message || 'Generation failed.');
        setStep(1);
      }
    }
  };

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const sendParentEmail = async () => {
    if (!parentEmail || !sections) return;
    setSendingParent(true);
    try {
      await liteApi.emailParentHandoff({
        parent_email: parentEmail,
        parent_handoff: sections.parent_handoff,
        home_carryover: sections.home_carryover,
        clinician_name: clinicianName,
        clinic_name: clinicName,
      });
      toast.success(`Sent to ${parentEmail}`);
      setShowParentEmail(false);
      setParentEmail('');
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setSendingParent(false);
    }
  };

  return (
    <LiteShell currentStep={step}>
      <h1 className="text-3xl font-serif text-primary-900 mb-2">Session-to-Home Bridge</h1>
      <p className="text-stone-500 mb-6">
        Turn today's session into clear next steps — for therapy and for home.
      </p>
      <StepIndicator step={step} />

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="session-bridge-error">
          {error}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <IframeDictationNotice inIframe={dictationInIframe} />
          <DictationError error={dictationError} inIframe={dictationInIframe} onDismiss={dismissDictationError} />

          {/* 1. Session notes */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                What happened in the session? <span className="text-red-500">*</span>
              </Label>
              {voiceSupported && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => startDictation('session_notes')}
                  className={`h-6 px-2 rounded-full text-xs ${recordingField === 'session_notes' ? 'text-red-600 bg-red-50' : 'text-stone-500'}`}
                  data-testid="session-bridge-notes-dictate"
                >
                  <Mic className={`w-3 h-3 mr-1 ${recordingField === 'session_notes' ? 'animate-pulse' : ''}`} />
                  {recordingField === 'session_notes' ? 'Listening…' : 'Dictate'}
                </Button>
              )}
            </div>
            <Textarea
              placeholder="What stood out today? What did the child enjoy, communicate, avoid, initiate, regulate around, or respond to?"
              rows={5}
              value={form.session_notes}
              onChange={(e) => update('session_notes', e.target.value)}
              data-testid="session-bridge-session-notes"
              className="resize-none"
            />
          </div>

          {/* 2. Child interests */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Child interests today</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.interests.map((chip, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => removeInterest(i)}
                  data-testid={`session-bridge-interest-chip-${i}`}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary-800 text-sm rounded-full px-3 py-1 hover:bg-primary/20 transition"
                >
                  {chip}
                  <X className="w-3 h-3" />
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Type an interest and press Enter"
                value={form.interest_input}
                onChange={(e) => update('interest_input', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addInterest(form.interest_input);
                  }
                }}
                data-testid="session-bridge-interest-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addInterest(form.interest_input)}
                data-testid="session-bridge-interest-add"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {INTEREST_SUGGESTIONS.filter((s) => !form.interests.includes(s)).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => addInterest(s)}
                  className="text-xs text-stone-500 hover:text-primary bg-stone-50 hover:bg-primary/5 border border-stone-200 rounded-full px-2.5 py-0.5 transition"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Communication profile + 4. Regulation + Session duration */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Communication profile</Label>
              <Select value={form.communication_profile} onValueChange={(v) => update('communication_profile', v)}>
                <SelectTrigger data-testid="session-bridge-profile-select">
                  <SelectValue placeholder="Pick one (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {COMMUNICATION_PROFILES.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`session-bridge-profile-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Regulation / engagement today</Label>
              <Select value={form.regulation} onValueChange={(v) => update('regulation', v)}>
                <SelectTrigger data-testid="session-bridge-regulation-select">
                  <SelectValue placeholder="Pick one (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {REGULATION_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`session-bridge-regulation-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-stone-400" />
                Session duration
              </Label>
              <Select value={form.session_duration} onValueChange={(v) => update('session_duration', v)}>
                <SelectTrigger data-testid="session-bridge-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`session-bridge-duration-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 5. Parent concerns */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Anything the parent mentioned? <span className="text-stone-400 font-normal">(optional)</span></Label>
              {voiceSupported && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => startDictation('parent_input')}
                  className={`h-6 px-2 rounded-full text-xs ${recordingField === 'parent_input' ? 'text-red-600 bg-red-50' : 'text-stone-500'}`}
                  data-testid="session-bridge-parent-dictate"
                >
                  <Mic className={`w-3 h-3 mr-1 ${recordingField === 'parent_input' ? 'animate-pulse' : ''}`} />
                  {recordingField === 'parent_input' ? 'Listening…' : 'Dictate'}
                </Button>
              )}
            </div>
            <Textarea
              placeholder="difficulty with dressing, bedtime, sibling frustration, wanting more words…"
              rows={2}
              value={form.parent_input}
              onChange={(e) => update('parent_input', e.target.value)}
              data-testid="session-bridge-parent-input"
              className="resize-none"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={goToDeId} className="rounded-full" data-testid="session-bridge-next-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Generate next steps
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <DeIdPanel
          text={buildNotesText()}
          onProceed={proceedAfterDeId}
          onCancel={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-600" data-testid="session-bridge-generating">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="font-medium">Writing the bridge…</p>
          <p className="text-sm text-stone-500 mt-1">Usually 5–15 seconds.</p>
        </div>
      )}

      {step === 4 && sections && (
        <div className="space-y-5" data-testid="session-bridge-output">
          {usage && (
            <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>{usage.daily_limit - usage.used_today} of {usage.daily_limit} free generations left today</span>
            </div>
          )}

          <OutputSection
            icon={<FileText className="w-4 h-4" />}
            label="Clinical reflection"
            color="stone"
            body={sections.clinical_reflection}
            sectionKey="clinical"
            onCopy={copyText}
            copiedKey={copiedKey}
            testId="section-clinical"
          />
          <OutputSection
            icon={<Sparkles className="w-4 h-4" />}
            label="Suggested next session focus"
            color="amber"
            body={sections.next_session_focus}
            sectionKey="next"
            onCopy={copyText}
            copiedKey={copiedKey}
            testId="section-next"
          />
          <OutputSection
            icon={<Users className="w-4 h-4" />}
            label="Parent handoff"
            color="emerald"
            body={sections.parent_handoff}
            sectionKey="parent"
            onCopy={copyText}
            copiedKey={copiedKey}
            testId="section-parent"
            rightAction={
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowParentEmail(true)}
                data-testid="session-bridge-email-parent-btn"
                className="rounded-full"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Email to parent
              </Button>
            }
          />
          <OutputSection
            icon={<Home className="w-4 h-4" />}
            label="Home carry-over idea"
            color="violet"
            body={sections.home_carryover}
            sectionKey="home"
            onCopy={copyText}
            copiedKey={copiedKey}
            testId="section-home"
          />

          <div className="flex items-center gap-2 pt-3 border-t border-stone-200">
            <Button
              variant="outline"
              onClick={() => { setOutput(''); setSections(null); setStep(1); }}
              data-testid="session-bridge-new-btn"
              className="rounded-full"
            >
              Start new
            </Button>
            <Button
              variant="ghost"
              onClick={() => runGenerate()}
              data-testid="session-bridge-regenerate-btn"
              className="rounded-full"
            >
              Regenerate
            </Button>
            <div className="flex-1" />
            <Button
              variant="ghost"
              onClick={() => copyText('all', output)}
              data-testid="session-bridge-copy-all-btn"
              className="rounded-full text-xs"
            >
              {copiedKey === 'all' ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              Copy all
            </Button>
          </div>

          {/* Anonymous thumbs up/down feedback */}
          <LiteFeedback tool="session-bridge" outputKey={(output || '').slice(0, 80)} />
        </div>
      )}

      {/* Email-to-parent modal */}
      {showParentEmail && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !sendingParent && setShowParentEmail(false)}
          data-testid="session-bridge-parent-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-bridge-parent-modal-title"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 id="session-bridge-parent-modal-title" className="text-lg font-semibold text-stone-900">Email to parent</h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Only the parent handoff + home idea will be sent. Your clinical reflection stays with you.
                </p>
              </div>
              <button
                onClick={() => !sendingParent && setShowParentEmail(false)}
                className="text-stone-400 hover:text-stone-600"
                data-testid="session-bridge-parent-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Parent email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  placeholder="parent@example.com"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  data-testid="session-bridge-parent-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Your name <span className="text-stone-400 font-normal">(optional)</span></Label>
                  <Input
                    placeholder="Michaela, SLP"
                    value={clinicianName}
                    onChange={(e) => setClinicianName(e.target.value)}
                    data-testid="session-bridge-clinician-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Practice <span className="text-stone-400 font-normal">(optional)</span></Label>
                  <Input
                    placeholder="PlayFirst Therapy"
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                    data-testid="session-bridge-clinic-input"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 leading-relaxed">
                <strong>Privacy:</strong> the email contains only what's shown in "Parent handoff" and "Home carry-over idea".
                No clinical reflection, no session notes, no client name. PlayFirst doesn't store the content.
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowParentEmail(false)}
                  disabled={sendingParent}
                  className="flex-1"
                  data-testid="session-bridge-parent-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendParentEmail}
                  disabled={sendingParent || !parentEmail}
                  className="flex-1"
                  data-testid="session-bridge-parent-send-btn"
                >
                  {sendingParent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RateLimitGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        used={gateInfo.used}
        limit={gateInfo.limit}
        onUnlocked={() => setGateOpen(false)}
      />
    </LiteShell>
  );
}

function OutputSection({ icon, label, color, body, sectionKey, onCopy, copiedKey, testId, rightAction }) {
  const colors = {
    stone: 'bg-stone-50 border-stone-200 text-stone-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  };
  const bubble = colors[color] || colors.stone;
  const isCopied = copiedKey === sectionKey;

  // Render markdown-like body
  const lines = (body || '').split('\n');
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={testId}>
      <div className={`px-4 py-2.5 flex items-center justify-between border-b border-stone-200 ${bubble}`}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {label}
        </div>
        <div className="flex items-center gap-2">
          {rightAction}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCopy(sectionKey, body)}
            className="rounded-full h-7 text-xs"
            data-testid={`${testId}-copy`}
          >
            {isCopied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <div className="px-4 py-4 text-sm text-stone-700 leading-relaxed space-y-2">
        {lines.map((raw, i) => {
          const line = raw.trim();
          if (!line) return null;
          if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ')) {
            return <li key={i} className="ml-5 list-disc">{line.replace(/^[-*•]\s*/, '')}</li>;
          }
          return <p key={i}>{line}</p>;
        })}
      </div>
    </div>
  );
}
